import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useUpdatePatient } from '_api';
import { Guardian, Patient, PatientPayload, Therapist } from '_api/types';
import { ACCOUNT_TYPE, PatientRouteMap, renderAge } from '_constants';
import { SEVERITY } from '_constants/derived';
import { TextButton, ThumbnailView } from 'components/atoms';
import {
  PatientCardText,
  PatientHeader,
  PatientHeaderSubtext,
  PatientSubHeader,
} from 'components/atoms/typographyStyles';
import { AlertMessage, BaseModal } from 'components/molecules';
import React, { useMemo, useState } from 'react';
import { CgProfile } from 'react-icons/cg';
import { FaEdit } from 'react-icons/fa';
import { FaUserXmark } from 'react-icons/fa6';
import { IoArrowBackOutline } from 'react-icons/io5';
import { MdEmail } from 'react-icons/md';
import { useNavigateTo } from 'utils';
import { useUser } from 'utils/hooks';

import { InfoSimpleModal, NewMessageModal } from './modals';
import { PatientCardProps } from './types';

export const PatientCard = ({ patient }: PatientCardProps) => {
  const { account, isTherapist } = useUser();
  const navigate = useNavigateTo();
  const { mutateAsync: updatePatient } = useUpdatePatient();
  const [confirmRemoveTherapist, setConfirmRemoveTherapist] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const handleCloseRemoveTherapist = () => setConfirmRemoveTherapist(false);
  const [openNewMessage, setOpenNewMessage] = useState(false);
  const {
    id: guardianId,
    firstName: guardianFirstName,
    lastName: guardianLastName,
    email: guardianEmail,
    phoneNumber: guardianPhoneNumber,
  } = patient.guardian || ({} as Guardian);

  const showMessages = useMemo(() => {
    if (account && account.accountType == ACCOUNT_TYPE.THERAPIST) {
      return (account as Therapist).hospital.canGuardianContactTherapist != false;
    }
    return false;
  }, [account]);

  const renderDiagnosis = () => {
    return patient.diagnosis.map(
      (diag, i) => `${diag.description}${i < patient.diagnosis.length - 1 ? ', ' : ''}`,
    );
  };

  const renderTherapists = () => {
    return patient.therapists.map(
      (therapist, i) =>
        `${therapist.firstName} ${therapist.lastName}${i < patient.therapists.length - 1 ? ', ' : ''}`,
    );
  };

  const handleEditOnClick = (id: number) => {
    navigate(PatientRouteMap.EDIT, id.toString());
  };

  const handleRemoveTherapistFromPatient = async (patient: Patient) => {
    let therapistIndex = -1;
    if (patient.therapists.length > 0 && account) {
      therapistIndex = patient.therapists.findIndex(
        (therapists) => therapists?.id === account.id,
      );
    }

    if (therapistIndex != -1) {
      patient.therapists.splice(therapistIndex, 1);
      const therapistsArray = patient.therapists.map((x) => x.id);
      const diagnosisArray = patient.diagnosis.map((x) => x.name);
      const payload: PatientPayload = {
        id: patient.id,
        firstName: patient.firstName,
        lastName: patient.lastName,
        dateOfBirth: patient.dateOfBirth,
        mrn: patient.mrn,
        access: patient.access,
        imageUrl: patient.imageUrl,
        points: patient.points,
        secretWord: patient.secretWord,
        themeIndex: patient.themeIndex,
        diagnosis: diagnosisArray,
        therapistIds: therapistsArray,
      };
      return await updatePatient(payload);
    }
  };

  return (
    <Box sx={{ overflow: 'auto' }}>
      <Box>
        <TextButton onClick={() => history.back()}>
          <IoArrowBackOutline />
          BACK
        </TextButton>
      </Box>
      <Box alignItems="center" display="flex" flexDirection="column">
        {patient && patient.fullImageUrl ? (
          <ThumbnailView
            fullImageUrl={patient.fullImageUrl}
            imageStyle={{ height: 'auto' }}
          />
        ) : (
          <CgProfile />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <PatientHeader gutterBottom sx={{ marginBottom: 10, paddingRight: '10px' }}>
            {patient.firstName} {patient.lastName}
          </PatientHeader>
          <span>
            <IconButton
              onClick={() => handleEditOnClick(Number(patient.id))}
              size="medium"
              sx={{ paddingBottom: 5 }}
            >
              <FaEdit />
            </IconButton>
          </span>
          {isTherapist && (
            <Tooltip title={'Remove this patient from your profile'} placement={'top'}>
              <span>
                <IconButton
                  onClick={() => setConfirmRemoveTherapist(true)}
                  size="medium"
                  sx={{ paddingBottom: 5 }}
                >
                  <FaUserXmark />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        <PatientHeaderSubtext>Patient ID: {patient.id}</PatientHeaderSubtext>
        <PatientHeaderSubtext>MRN: {patient?.mrn}</PatientHeaderSubtext>
      </Box>
      <hr />
      <Box sx={{ marginBottom: '16px' }}>
        <PatientSubHeader>AGE</PatientSubHeader>
        <PatientCardText>{renderAge(patient.dateOfBirth)}</PatientCardText>
      </Box>
      <Box sx={{ marginBottom: '16px' }}>
        <PatientSubHeader>THERAPIST</PatientSubHeader>
        <PatientCardText>{renderTherapists()}</PatientCardText>
      </Box>
      <Box sx={{ marginBottom: '16px' }}>
        <PatientSubHeader>DIAGNOSIS</PatientSubHeader>
        <PatientCardText>{renderDiagnosis()}</PatientCardText>
      </Box>
      <hr />
      <Box sx={{ marginBottom: '16px' }}>
        <PatientSubHeader>GUARDIAN</PatientSubHeader>
        <PatientCardText>{guardianFirstName + ' ' + guardianLastName}</PatientCardText>
      </Box>
      <Box sx={{ marginBottom: '16px' }}>
        <PatientSubHeader>PHONE</PatientSubHeader>
        <PatientCardText>{guardianPhoneNumber}</PatientCardText>
      </Box>
      <Box>
        <PatientSubHeader>EMAIL</PatientSubHeader>
        <Box
          sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}
        >
          <PatientCardText>{guardianEmail}</PatientCardText>
          {account && account.accountType === ACCOUNT_TYPE.THERAPIST ? (
            <span style={{ float: 'right' }}>
              <IconButton onClick={() => setOpenNewMessage(true)} size="small">
                <MdEmail />
              </IconButton>
            </span>
          ) : null}
        </Box>
      </Box>
      {showMessages ? (
        <NewMessageModal
          defaultReceiverId={guardianId}
          defaultReceiverText={`${guardianFirstName} ${guardianLastName} - ${patient.firstName} ${patient.lastName}`}
          open={openNewMessage}
          onClose={() => setOpenNewMessage(false)}
        />
      ) : (
        <BaseModal
          open={openNewMessage}
          onClose={() => setOpenNewMessage(false)}
          title={'Messaging Not Available'}
        >
          <Typography variant={'h6'} sx={{ textAlign: 'left' }}>
            Messaging is not available for this organization. Please contact your hospital
            administrator with any questions
          </Typography>
        </BaseModal>
      )}

      <InfoSimpleModal
        icon={FaUserXmark}
        open={confirmRemoveTherapist}
        onClose={handleCloseRemoveTherapist}
        onSubmit={() => {
          handleRemoveTherapistFromPatient(patient)
            .then((result) => {
              if (result) {
                setShowSuccess(true);
                setConfirmRemoveTherapist(false);
                setTimeout(() => {
                  setShowSuccess(false);
                  navigate(PatientRouteMap.TABLE);
                }, 2000);
              }
            })
            .catch((err) => {
              setErrorMessage(true);
              setShowError(true);
              setTimeout(() => {
                setErrorMessage(err.message);
              }, 2000);
            });
        }}
        successButton={false}
        text={[`You are about to remove this patient from your profile`, 'Are you sure?']}
        title="WARNING"
      />
      <AlertMessage
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={'Successfully removed patient from therapist'}
      />
      <AlertMessage
        open={!!errorMessage || showError}
        onClose={() => setShowError(false)}
        severity={SEVERITY.error}
        message={'Failed to remove patient from therapist.' + errorMessage}
      />
    </Box>
  );
};
